import React, { useState, useEffect } from 'react'
import { BarChart3, RefreshCw, Calendar, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { supabase } from '../../lib/supabase'

interface ReportStatusSectionProps {
  settings: {
    lastDailyReport: string | null
    lastWeeklyReport: string | null
    sendTime: string
  }
  onRefresh: () => void
  isAccordion?: boolean
}

interface ReportLog {
  id: string
  type: 'daily' | 'weekly'
  sent_at: string
  recipients_count: number
  status: 'sent' | 'failed'
  error_message?: string
}

export const ReportStatusSection: React.FC<ReportStatusSectionProps> = ({
  settings,
  onRefresh,
  isAccordion = false
}) => {
  const [reportsLog, setReportsLog] = useState<ReportLog[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [nextReportTime, setNextReportTime] = useState<string>('')

  useEffect(() => {
    loadReportsLog()
    calculateNextReportTime()
  }, [settings.sendTime])

  const loadReportsLog = async () => {
    try {
      setIsLoading(true)
      const { data, error } = await supabase
        .from('email_reports_log')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(10)

      if (error) {
        console.error('Error loading reports log:', error)
        return
      }

      setReportsLog(data || [])
    } catch (error) {
      console.error('Error loading reports log:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const calculateNextReportTime = () => {
    if (!settings.sendTime) return

    const now = new Date()
    const today = new Date(now)
    const [hours, minutes] = settings.sendTime.split(':').map(Number)
    
    today.setHours(hours, minutes, 0, 0)
    
    // If time has passed today, schedule for tomorrow
    if (today <= now) {
      today.setDate(today.getDate() + 1)
    }
    
    setNextReportTime(today.toLocaleString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle size={16} className="text-green-500" />
      case 'failed':
        return <XCircle size={16} className="text-red-500" />
      default:
        return <AlertCircle size={16} className="text-yellow-500" />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'sent':
        return 'Verstuurd'
      case 'failed':
        return 'Mislukt'
      default:
        return 'Onbekend'
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'daily':
        return 'Dagelijks'
      case 'weekly':
        return 'Wekelijks'
      default:
        return type
    }
  }

  return (
    <div className={isAccordion ? '' : 'bg-white rounded-lg shadow-md p-6'}>
      {!isAccordion && (
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <BarChart3 className="text-kvh-red" />
            Rapport Status
          </h2>
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            Ververs
          </button>
        </div>
      )}
      {isAccordion && (
        <div className="flex justify-end mb-4">
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            Ververs
          </button>
        </div>
      )}
      
      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="font-semibold text-green-800 flex items-center gap-2">
            <Calendar size={16} />
            Laatste Dagrapport
          </h3>
          <p className="text-green-600 text-sm mt-1">
            {settings.lastDailyReport 
              ? formatDate(settings.lastDailyReport)
              : 'Nog niet verstuurd'
            }
          </p>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-800 flex items-center gap-2">
            <Calendar size={16} />
            Laatste Weekrapport
          </h3>
          <p className="text-blue-600 text-sm mt-1">
            {settings.lastWeeklyReport 
              ? formatDate(settings.lastWeeklyReport)
              : 'Nog niet verstuurd'
            }
          </p>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Clock size={16} />
            Volgende Rapport
          </h3>
          <p className="text-gray-600 text-sm mt-1">
            {nextReportTime || 'Niet gepland'}
          </p>
        </div>
      </div>
      
      {/* Recent Reports Log */}
      <div>
        <h3 className="font-semibold text-gray-800 mb-3">Recente Rapporten</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Datum</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Ontvangers</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-kvh-red"></div>
                      Laden...
                    </div>
                  </td>
                </tr>
              ) : reportsLog.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-gray-500">
                    Geen rapporten gevonden
                  </td>
                </tr>
              ) : (
                reportsLog.map((report) => (
                  <tr key={report.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2">{formatDate(report.sent_at)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        report.type === 'daily' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {getTypeLabel(report.type)}
                      </span>
                    </td>
                    <td className="px-3 py-2">{report.recipients_count}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        {getStatusIcon(report.status)}
                        <span className={
                          report.status === 'sent' 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }>
                          {getStatusText(report.status)}
                        </span>
                      </div>
                      {report.error_message && (
                        <div className="text-xs text-red-500 mt-1">
                          {report.error_message}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}